import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";
import SearchForm from "./SearchForm";
import ResultsTable from "./ResultsTable";
import SavedLists from "./SavedLists";
import PushModal from "./PushModal";
import SettingsModal from "./SettingsModal";
import BlocklistModal from "./BlocklistModal";
import Digest from "./Digest";
import { useToast } from "../../hooks/use-toast";
import {
  searchLeads, getHistory, saveList, leadsToCSV, downloadCSV, getConfig, resetSeen,
  getSettings, getPresets, savePreset, deletePreset, getBlocklist, addBlock,
} from "../../lib/api";
import {
  Download, Bookmark, ChevronLeft, ChevronRight, Info, Clock, X, Search as SearchIcon,
  Mail, RotateCcw, Send, Star, Layers, Loader2, Ban, PlayCircle,
} from "lucide-react";

export default function LeadFinder() {
  const { toast } = useToast();
  const [view, setView] = useState("find");
  const [industry, setIndustry] = useState("Plumber");
  const [city, setCity] = useState("Sacramento");
  const [state, setState] = useState("CA");
  const [page, setPage] = useState(0);
  const [results, setResults] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState({});
  const [history, setHistory] = useState([]);
  const [keyConfigured, setKeyConfigured] = useState(true);
  const [instantlyConfigured, setInstantlyConfigured] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [listName, setListName] = useState("");
  const [emailOnly, setEmailOnly] = useState(false);
  const [dedupe, setDedupe] = useState(true);
  const [meta, setMeta] = useState({ fetched: 0, hidden_dupes: 0, hidden_no_email: 0, hidden_blocked: 0 });
  const [pushOpen, setPushOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [presets, setPresets] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockCount, setBlockCount] = useState(0);

  const loadHistory = () => getHistory().then(setHistory).catch(() => {});
  const loadPresets = () => getPresets().then(setPresets).catch(() => {});
  const loadBlockCount = () => getBlocklist().then((d) => setBlockCount(d.length)).catch(() => {});

  useEffect(() => {
    getConfig().then((c) => {
      setKeyConfigured(c.key_configured);
      setInstantlyConfigured(c.instantly_configured);
    }).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
    loadHistory();
    loadPresets();
    loadBlockCount();
  }, []);

  const runSearch = async (pageNum = 0, ind = industry, ci = city, st = state, eo = emailOnly, dd = dedupe) => {
    setLoading(true);
    try {
      const data = await searchLeads({ industry: ind, city: ci, state: st, page: pageNum, emailOnly: eo, dedupe: dd });
      setResults(data.results);
      setSource(data.source);
      setMeta({ fetched: data.fetched, hidden_dupes: data.hidden_dupes, hidden_no_email: data.hidden_no_email, hidden_blocked: data.hidden_blocked });
      setPage(pageNum);
      setSelected({});
      setHasSearched(true);
      loadHistory();
    } catch (e) {
      toast({ title: "Search failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const runBulk = async (eo = emailOnly, dd = dedupe) => {
    const cities = city.split(",").map((c) => c.trim()).filter(Boolean);
    if (!industry.trim() || !cities.length) return;
    setLoading(true);
    setHasSearched(true);
    setResults([]);
    setSelected({});
    const combined = [];
    for (let i = 0; i < cities.length; i++) {
      setBulkProgress(`Searching ${cities[i]} — city ${i + 1} of ${cities.length}…`);
      try {
        const data = await searchLeads({ industry, city: cities[i], state, page: 0, emailOnly: eo, dedupe: dd });
        combined.push(...data.results.map((r) => ({ ...r, _city: cities[i] })));
        setResults([...combined]);
      } catch (e) { /* keep going on per-city errors */ }
    }
    setSource("outscraper");
    setMeta({ fetched: combined.length, hidden_dupes: 0, hidden_no_email: 0 });
    setPage(0);
    setBulkProgress("");
    setLoading(false);
    loadHistory();
  };

  const doSearch = () => (bulkMode ? runBulk() : runSearch(0));

  const toggleEmailOnly = () => {
    const v = !emailOnly;
    setEmailOnly(v);
    if (hasSearched) bulkMode ? runBulk(v, dedupe) : runSearch(0, industry, city, state, v, dedupe);
  };
  const toggleDedupe = () => {
    const v = !dedupe;
    setDedupe(v);
    if (hasSearched) bulkMode ? runBulk(emailOnly, v) : runSearch(0, industry, city, state, emailOnly, v);
  };
  const doResetSeen = async () => {
    const r = await resetSeen();
    toast({ title: "Dedupe memory cleared", description: `${r.cleared} companies forgotten.` });
  };

  const selectedLeads = useMemo(() => results.filter((r) => selected[r.id]), [results, selected]);
  const selectedCount = selectedLeads.length;
  const emailCount = results.filter((r) => r.email).length;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (results.every((r) => selected[r.id])) setSelected({});
    else setSelected(Object.fromEntries(results.map((r) => [r.id, true])));
  };

  const exportCSV = () => {
    const leads = selectedCount > 0 ? selectedLeads : results;
    if (!leads.length) return;
    const fname = `${industry}_${city}_${state}.csv`.replace(/[\s,]+/g, "-").toLowerCase();
    downloadCSV(fname, leadsToCSV(leads, settings));
    toast({ title: "Exported", description: `${leads.length} leads downloaded (Instantly format).` });
  };

  const doSave = async () => {
    const leads = selectedCount > 0 ? selectedLeads : results;
    if (!leads.length || !listName.trim()) return;
    try {
      await saveList(listName.trim(), leads);
      toast({ title: "List saved", description: `"${listName}" with ${leads.length} leads.` });
      setSaveOpen(false);
      setListName("");
    } catch (e) {
      toast({ title: "Could not save", variant: "destructive" });
    }
  };

  const saveCurrentPreset = async () => {
    if (!industry.trim() || !city.trim()) return;
    try {
      await savePreset({ industry: industry.trim(), city: city.trim(), state });
      toast({ title: "Preset saved", description: `${industry} · ${city}` });
      loadPresets();
    } catch (e) { toast({ title: "Could not save preset", variant: "destructive" }); }
  };
  const applyPreset = (p) => {
    setIndustry(p.industry); setCity(p.city); setState(p.state || "CA");
    setBulkMode(p.city.includes(","));
    p.city.includes(",")
      ? runBulk(emailOnly, dedupe)
      : runSearch(0, p.industry, p.city, p.state || "CA", emailOnly, dedupe);
  };
  const removePreset = async (id, e) => {
    e.stopPropagation();
    await deletePreset(id);
    loadPresets();
  };

  const runAllPresets = async () => {
    if (!presets.length) return;
    setLoading(true);
    setHasSearched(true);
    setResults([]);
    setSelected({});
    const combined = [];
    for (let pi = 0; pi < presets.length; pi++) {
      const p = presets[pi];
      const cities = p.city.split(",").map((c) => c.trim()).filter(Boolean);
      for (let ci = 0; ci < cities.length; ci++) {
        setBulkProgress(`Preset ${pi + 1}/${presets.length}: ${p.industry} — ${cities[ci]}…`);
        try {
          const data = await searchLeads({ industry: p.industry, city: cities[ci], state: p.state, page: 0, emailOnly, dedupe });
          combined.push(...data.results.map((r) => ({ ...r, _city: cities[ci] })));
          setResults([...combined]);
        } catch (e) { /* keep going */ }
      }
    }
    setSource("outscraper");
    setMeta({ fetched: combined.length, hidden_dupes: 0, hidden_no_email: 0, hidden_blocked: 0 });
    setPage(0);
    setBulkProgress("");
    setLoading(false);
    loadHistory();
  };

  const blockLead = async (lead) => {
    try {
      await addBlock(lead.name, "company");
      setResults((rs) => rs.filter((r) => r.id !== lead.id));
      loadBlockCount();
      toast({ title: "Blocked", description: `${lead.name} won't appear again.` });
    } catch (e) { toast({ title: "Could not block", variant: "destructive" }); }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <Header view={view} setView={setView} onSettings={() => setSettingsOpen(true)} />

      {view === "lists" ? (
        <SavedLists settings={settings} instantlyConfigured={instantlyConfigured} />
      ) : view === "digest" ? (
        <Digest />
      ) : (
        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="mb-6">
            <h1 className="font-display text-[26px] font-bold text-foreground">Find local business leads</h1>
            <p className="text-[14px] text-muted-foreground mt-1">Search any industry in any U.S. city. Get company name, address, phone & email — 20 at a time.</p>
          </div>

          {!keyConfigured && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span><b>Sample data mode.</b> Add your Outscraper API key to pull real, live businesses & emails.</span>
            </div>
          )}

          {/* Bulk toggle */}
          <div className="mb-3 flex items-center gap-2">
            <button onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2">
              <span className={`w-9 h-5 rounded-full transition-colors relative ${bulkMode ? "bg-brand-600" : "bg-gray-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${bulkMode ? "left-[18px]" : "left-0.5"}`} />
              </span>
              <span className="text-[13px] font-medium text-foreground inline-flex items-center gap-1"><Layers size={14} /> Bulk cities</span>
              <span className="text-[12px] text-muted-foreground">— search a whole region at once</span>
            </button>
          </div>

          <SearchForm
            industry={industry} city={city} state={state}
            setIndustry={setIndustry} setCity={setCity} setState={setState}
            onSearch={doSearch} loading={loading} bulkMode={bulkMode}
          />

          {bulkMode && (
            <p className="mt-2 px-1 text-[12px] text-muted-foreground">
              Enter multiple cities separated by commas (e.g. <b>Sacramento, Roseville, Folsom</b>). Each city is a separate live search (~1–3 min each).
            </p>
          )}

          {/* Filters */}
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 px-1">
            <button onClick={toggleEmailOnly} className="flex items-center gap-2">
              <span className={`w-9 h-5 rounded-full transition-colors relative ${emailOnly ? "bg-brand-600" : "bg-gray-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${emailOnly ? "left-[18px]" : "left-0.5"}`} />
              </span>
              <span className="text-[13px] font-medium text-foreground">Email only</span>
            </button>
            <button onClick={toggleDedupe} className="flex items-center gap-2">
              <span className={`w-9 h-5 rounded-full transition-colors relative ${dedupe ? "bg-brand-600" : "bg-gray-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${dedupe ? "left-[18px]" : "left-0.5"}`} />
              </span>
              <span className="text-[13px] font-medium text-foreground">Dedupe guard</span>
            </button>
            {dedupe && (
              <button onClick={doResetSeen} className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-brand-600 transition-colors">
                <RotateCcw size={13} /> Reset seen memory
              </button>
            )}
            <button onClick={() => setBlockOpen(true)} className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-red-500 transition-colors">
              <Ban size={13} /> Do-not-contact{blockCount > 0 ? ` (${blockCount})` : ""}
            </button>
            <button onClick={saveCurrentPreset} className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-brand-600 transition-colors ml-auto">
              <Star size={13} /> Save as preset
            </button>
          </div>

          {/* Presets */}
          {presets.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground"><Star size={13} /> Presets</div>
                <button onClick={runAllPresets} disabled={loading}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-full px-3 py-1.5 hover:bg-brand-100 disabled:opacity-50 transition-colors">
                  <PlayCircle size={14} /> Run all presets
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <span key={p.id} onClick={() => applyPreset(p)}
                    className="group cursor-pointer inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full bg-white border border-border hover:border-brand-400 transition-colors">
                    {p.city.includes(",")
                      ? `${p.industry} · ${p.city.split(",").filter((c) => c.trim()).length} cities${p.state ? `, ${p.state}` : ""}`
                      : `${p.industry} · ${p.city}${p.state ? `, ${p.state}` : ""}`}
                    <button onClick={(e) => removePreset(p.id, e)} className="text-muted-foreground/40 hover:text-red-500"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {history.length > 0 && !hasSearched && (
            <div className="mt-5">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground mb-2"><Clock size={13} /> Recent searches</div>
              <div className="flex flex-wrap gap-2">
                {history.map((h, i) => (
                  <button key={i}
                    onClick={() => { setIndustry(h.industry); setCity(h.city); setState(h.state); setBulkMode(false); runSearch(0, h.industry, h.city, h.state); }}
                    className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-border hover:border-brand-400 transition-colors">
                    {h.industry} · {h.city}, {h.state}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && bulkProgress && (
            <div className="mt-6 flex items-center gap-2 text-[14px] text-muted-foreground">
              <Loader2 size={16} className="animate-spin text-brand-600" /> {bulkProgress}
            </div>
          )}

          {hasSearched && (
            <div className="mt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 text-[13px] text-muted-foreground flex-wrap">
                  <span className="font-medium text-foreground">{results.length} results</span>
                  <span>· {emailCount} with email</span>
                  {meta.hidden_dupes > 0 && <span className="text-[11px] bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{meta.hidden_dupes} dupes hidden</span>}
                  {meta.hidden_no_email > 0 && <span className="text-[11px] bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{meta.hidden_no_email} no-email hidden</span>}
                  {meta.hidden_blocked > 0 && <span className="text-[11px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{meta.hidden_blocked} blocked hidden</span>}
                  {selectedCount > 0 && <span className="text-brand-600 font-medium">· {selectedCount} selected</span>}
                  {source === "outscraper" && <span className="text-[11px] bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full">live</span>}
                  {source === "sample" && <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">sample</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSaveOpen(true)} className="inline-flex items-center gap-1.5 text-[13px] font-medium border border-border rounded-lg px-3.5 py-2 hover:bg-secondary transition-colors">
                    <Bookmark size={15} /> Save {selectedCount > 0 ? `(${selectedCount})` : "all"}
                  </button>
                  {instantlyConfigured && (
                    <button onClick={() => setPushOpen(true)} className="inline-flex items-center gap-1.5 text-[13px] font-medium border border-brand-200 text-brand-700 bg-brand-50 rounded-lg px-3.5 py-2 hover:bg-brand-100 transition-colors">
                      <Send size={15} /> Push to Instantly
                    </button>
                  )}
                  <button onClick={exportCSV} className="inline-flex items-center gap-1.5 text-[13px] font-medium bg-brand-600 text-white rounded-lg px-3.5 py-2 hover:bg-brand-700 transition-colors">
                    <Download size={15} /> Export CSV
                  </button>
                </div>
              </div>

              {results.length > 0 ? (
                <>
                  <ResultsTable results={results} selected={selected} toggle={toggle} toggleAll={toggleAll} onBlock={blockLead} />
                  {!bulkMode && (
                    <div className="mt-4 flex items-center justify-between">
                      <button disabled={page === 0 || loading} onClick={() => runSearch(page - 1)}
                        className="inline-flex items-center gap-1 text-[13px] font-medium px-3.5 py-2 rounded-lg border border-border disabled:opacity-40 hover:bg-secondary transition-colors">
                        <ChevronLeft size={15} /> Previous
                      </button>
                      <span className="text-[13px] text-muted-foreground">Page {page + 1}</span>
                      <button disabled={results.length < 20 || loading} onClick={() => runSearch(page + 1)}
                        className="inline-flex items-center gap-1 text-[13px] font-medium px-3.5 py-2 rounded-lg border border-border disabled:opacity-40 hover:bg-secondary transition-colors">
                        Next 20 <ChevronRight size={15} />
                      </button>
                    </div>
                  )}
                </>
              ) : !loading ? (
                <div className="bg-white rounded-2xl border border-border p-12 text-center">
                  <SearchIcon size={28} className="mx-auto text-muted-foreground/40" />
                  <p className="mt-3 text-[14px] text-muted-foreground">No results found. Try a different industry or city.</p>
                </div>
              ) : null}
            </div>
          )}
        </main>
      )}

      {/* Save dialog */}
      {saveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSaveOpen(false)} />
          <div className="relative bg-white rounded-2xl border border-border w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-[18px] font-semibold text-foreground">Save lead list</h3>
              <button onClick={() => setSaveOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-[13px] text-muted-foreground mb-3">Saving {selectedCount > 0 ? selectedCount : results.length} leads.</p>
            <input autoFocus value={listName} onChange={(e) => setListName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSave()}
              placeholder="List name, e.g. Sacramento Plumbers"
              className="w-full border border-border rounded-lg px-3.5 py-2.5 text-[14px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSaveOpen(false)} className="px-4 py-2 rounded-lg text-[14px] text-muted-foreground hover:bg-secondary">Cancel</button>
              <button onClick={doSave} disabled={!listName.trim()} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-[14px] font-medium disabled:opacity-40 hover:bg-brand-700">Save list</button>
            </div>
          </div>
        </div>
      )}

      <PushModal open={pushOpen} onClose={() => setPushOpen(false)} leads={selectedCount > 0 ? selectedLeads : results} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={setSettings} />
      <BlocklistModal open={blockOpen} onClose={() => setBlockOpen(false)} onChange={setBlockCount} />
    </div>
  );
}
